import {
  campaignApiDelete,
  campaignApiGet,
  campaignApiPatch,
  campaignApiPost,
  type CampaignApiQueryValue,
} from '@/lib/api/campaign-api-client';
import type {
  ContactSummaryPagedResponseApi,
} from '@/modules/contacts/types/contacts-api.types';
import type { ContactsPagingResult } from '@/modules/contacts/types/contacts.types';
import { mapContactsRestResponse } from '@/modules/contacts/utils/contact-api-mapper';
import {
  appendRestFilterQuery,
  type ContactsRestFilters,
} from '@/modules/contacts/utils/contacts-rest-filter.utils';
import type {
  ContactSelectionFilterApi,
  CreateListMembershipJobRequestApi,
  CreateListRequestApi,
  ListApi,
  ListMembershipJobApi,
  ListMembershipJobEntityResponseApi,
  ListContactsPagedResponseEntityResponseApi,
  ListEntityResponseApi,
  ListMembersMutationResponseApi,
  ListMembersRequestApi,
  ListPagedResponseApi,
  ListPagedResponseEntityResponseApi,
  UpdateListRequestApi,
} from '../types/lists-api.types';
import type {
  ContactSelectAllSnapshot,
  CreateListRequest,
  GetListsParams,
  ListMembersMutationResult,
  ListMutationResult,
  ListsPagingResult,
  StartListMembershipJobParams,
  UpdateListRequest,
} from '../types/lists.types';
import { mapListApiToCampaignList, mapListItems, toListsPagingResult } from '../utils/list-api-mapper';

type CampaignApiEnvelope<T> = {
  success?: boolean;
  message?: string | null;
  errors?: string[] | null;
  data?: T;
};

const assertCampaignApiSuccess = <T>(
  response: CampaignApiEnvelope<T> | null | undefined,
  errorLabel: string
): T => {
  if (!response || typeof response !== 'object') {
    throw new Error(`${errorLabel}: invalid response structure`);
  }

  if (response.success === false) {
    const message =
      response.message ||
      (Array.isArray(response.errors) ? response.errors.join(', ') : undefined) ||
      errorLabel;
    throw new Error(message);
  }

  const data = response.data;
  if (data === undefined || data === null) {
    throw new Error(`${errorLabel}: missing data payload`);
  }

  return data;
};

const mutationMessage = (response: CampaignApiEnvelope<unknown> | null | undefined): string | undefined =>
  response?.message ?? undefined;

const toCreateBody = (input: CreateListRequest): CreateListRequestApi => ({
  name: input.name.trim(),
  description: input.description.trim(),
});

const toUpdateBody = (input: UpdateListRequest): UpdateListRequestApi => ({
  name: input.name.trim(),
  description: input.description.trim(),
});

export const getLists = async (params: GetListsParams = {}): Promise<ListsPagingResult> => {
  const pageNo = params.pageNo ?? 1;
  const pageSize = params.pageSize ?? 100;
  const offset = Math.max(0, (pageNo - 1) * pageSize);

  const response = await campaignApiGet<ListPagedResponseEntityResponseApi>('/v1/lists', {
    Limit: pageSize,
    Offset: offset,
  });

  const data = assertCampaignApiSuccess<ListPagedResponseApi | ListApi[]>(
    response,
    'Failed to fetch lists'
  );

  if (Array.isArray(data)) {
    const items = mapListItems(data);
    return toListsPagingResult(items, items.length, pageNo, pageSize);
  }

  const items = mapListItems(data.items);
  return toListsPagingResult(items, items.length, pageNo, pageSize, data.pageInfo);
};

export const getListById = async (listId: string) => {
  const id = listId.trim();
  if (!id) {
    throw new Error('List id is required');
  }

  const response = await campaignApiGet<ListEntityResponseApi>(`/v1/lists/${encodeURIComponent(id)}`);
  const data = assertCampaignApiSuccess(response, 'Failed to fetch list');
  const list = mapListApiToCampaignList(data);

  if (!list) {
    throw new Error(response.message || 'List not found');
  }

  return list;
};

export const createList = async (input: CreateListRequest): Promise<ListMutationResult> => {
  const body = toCreateBody(input);
  if (!body.name) {
    throw new Error('Name is required.');
  }

  const response = await campaignApiPost<ListEntityResponseApi>('/v1/lists', body);

  if (response && typeof response === 'object' && response.success === false) {
    const message =
      response.message ||
      (Array.isArray(response.errors) ? response.errors.join(', ') : undefined) ||
      'Failed to create list';
    throw new Error(message);
  }

  const list =
    mapListApiToCampaignList(response?.data) ??
    mapListApiToCampaignList(response as unknown as ListApi);
  const itemId = list?.id?.trim() || '';

  return {
    acknowledged: true,
    itemId: itemId || body.name,
    totalImpactedData: 1,
    list: list ?? undefined,
    message: mutationMessage(response),
  };
};

export const updateList = async (params: {
  listId: string;
  input: UpdateListRequest;
}): Promise<ListMutationResult> => {
  const listId = params.listId.trim();
  if (!listId) {
    throw new Error('List id is required');
  }

  const body = toUpdateBody(params.input);
  if (!body.name) {
    throw new Error('Name is required.');
  }

  const response = await campaignApiPatch<ListEntityResponseApi>(
    `/v1/lists/${encodeURIComponent(listId)}`,
    body
  );
  const data = assertCampaignApiSuccess(response, 'Failed to update list');
  const list = mapListApiToCampaignList(data);

  return {
    acknowledged: true,
    itemId: list?.id?.trim() || listId,
    totalImpactedData: 1,
    list: list ?? undefined,
    message: mutationMessage(response),
  };
};

export const deleteList = async (listId: string): Promise<ListMutationResult> => {
  const id = listId.trim();
  if (!id) {
    throw new Error('List id is required');
  }

  const response = await campaignApiDelete<ListEntityResponseApi>(
    `/v1/lists/${encodeURIComponent(id)}`
  );

  // HTTP 204 No Content is parsed as null by the shared client.
  if (response == null) {
    return {
      acknowledged: true,
      itemId: id,
      totalImpactedData: 1,
    };
  }

  if (typeof response !== 'object') {
    throw new Error('Failed to delete list: invalid response structure');
  }

  if (response.success === false) {
    const message =
      response.message ||
      (Array.isArray(response.errors) ? response.errors.join(', ') : undefined) ||
      'Failed to delete list';
    throw new Error(message);
  }

  const itemId = mapListApiToCampaignList(response.data)?.id?.trim() || id;

  return {
    acknowledged: true,
    itemId,
    totalImpactedData: 1,
    message: mutationMessage(response),
  };
};

export const getListContacts = async (params: {
  listId: string;
  pageNo?: number;
  pageSize?: number;
  q?: string;
  filter?: ContactsRestFilters;
}): Promise<ContactsPagingResult> => {
  const listId = params.listId.trim();
  if (!listId) {
    throw new Error('List id is required');
  }

  const pageNo = params.pageNo ?? 1;
  const pageSize = params.pageSize ?? 50;
  const offset = Math.max(0, (pageNo - 1) * pageSize);
  const query: Record<string, CampaignApiQueryValue> = {
    Limit: pageSize,
    Offset: offset,
  };
  const search = params.q?.trim();
  if (search) {
    query.Q = search;
  }

  const response = await campaignApiGet<ListContactsPagedResponseEntityResponseApi>(
    `/v1/lists/${encodeURIComponent(listId)}/contacts`,
    appendRestFilterQuery(query, params.filter)
  );
  const data = assertCampaignApiSuccess<ContactSummaryPagedResponseApi>(
    response,
    'Failed to fetch list members'
  );

  return mapContactsRestResponse(data.items, data.pageInfo, { pageNo, pageSize });
};

export const addListMembers = async (params: {
  listId: string;
  contactIds: string[];
}): Promise<ListMembersMutationResult> => {
  const listId = params.listId.trim();
  const contactIds = Array.from(new Set(params.contactIds.map((id) => id.trim()).filter(Boolean)));

  if (!listId) {
    throw new Error('List id is required');
  }
  if (contactIds.length === 0) {
    throw new Error('Select at least one contact');
  }

  const body: ListMembersRequestApi = { contactIds };
  const response = await campaignApiPost<ListMembersMutationResponseApi>(
    `/v1/lists/${encodeURIComponent(listId)}/members`,
    body
  );

  if (response && typeof response === 'object' && response.success === false) {
    const message =
      response.message ||
      (Array.isArray(response.errors) ? response.errors.join(', ') : undefined) ||
      'Failed to add members';
    throw new Error(message);
  }

  return {
    acknowledged: true,
    totalImpactedData: contactIds.length,
    message: mutationMessage(response),
  };
};

export const removeListMembers = async (params: {
  listId: string;
  contactIds: string[];
}): Promise<ListMembersMutationResult> => {
  const listId = params.listId.trim();
  const contactIds = Array.from(new Set(params.contactIds.map((id) => id.trim()).filter(Boolean)));

  if (!listId) {
    throw new Error('List id is required');
  }
  if (contactIds.length === 0) {
    throw new Error('Select at least one contact');
  }

  const body: ListMembersRequestApi = { contactIds };
  const response = await campaignApiDelete<ListMembersMutationResponseApi>(
    `/v1/lists/${encodeURIComponent(listId)}/members`,
    body
  );

  if (response && typeof response === 'object' && response.success === false) {
    const message =
      response.message ||
      (Array.isArray(response.errors) ? response.errors.join(', ') : undefined) ||
      'Failed to remove members';
    throw new Error(message);
  }

  return {
    acknowledged: true,
    totalImpactedData: contactIds.length,
    message: mutationMessage(response),
  };
};

export const addContactsToLists = async (params: {
  listIds: string[];
  contactIds: string[];
}): Promise<ListMembersMutationResult[]> => {
  const listIds = Array.from(new Set(params.listIds.map((id) => id.trim()).filter(Boolean)));
  const contactIds = Array.from(new Set(params.contactIds.map((id) => id.trim()).filter(Boolean)));

  if (listIds.length === 0) {
    throw new Error('No matching lists found for the selected contacts');
  }
  if (contactIds.length === 0) {
    throw new Error('At least one contact is required');
  }

  return Promise.all(listIds.map((listId) => addListMembers({ listId, contactIds })));
};

export const syncContactListMembership = async (params: {
  contactId: string;
  selectedListIds: string[];
  currentListIds: string[];
}): Promise<void> => {
  const contactId = params.contactId.trim();
  if (!contactId) {
    throw new Error('Contact id is required');
  }

  const selected = new Set(params.selectedListIds.map((id) => id.trim()).filter(Boolean));
  const current = new Set(params.currentListIds.map((id) => id.trim()).filter(Boolean));
  const toAdd = [...selected].filter((id) => !current.has(id));
  const toRemove = [...current].filter((id) => !selected.has(id));

  await Promise.all([
    ...toAdd.map((listId) => addListMembers({ listId, contactIds: [contactId] })),
    ...toRemove.map((listId) => removeListMembers({ listId, contactIds: [contactId] })),
  ]);
};

/**
 * The table's filter in the job body's shape. `source` is left out on purpose: `GET /v1/contacts`
 * takes no such parameter (see `ContactsRestFilters.source`), so the table never narrows by it.
 */
export const toContactSelectionFilter = (selection: ContactSelectAllSnapshot): ContactSelectionFilterApi => {
  const { filter } = selection;
  const q = selection.q?.trim();

  return {
    ...(q ? { q } : {}),
    ...(filter.emailStatus?.length ? { emailStatus: filter.emailStatus } : {}),
    ...(filter.smsStatus?.length ? { smsStatus: filter.smsStatus } : {}),
    ...(filter.lists?.length ? { lists: filter.lists } : {}),
    ...(filter.attributes && Object.keys(filter.attributes).length ? { filter: filter.attributes } : {}),
    ...(filter.attributeRanges && Object.keys(filter.attributeRanges).length
      ? { filterRange: filter.attributeRanges }
      : {}),
    ...(selection.suppressed !== undefined ? { suppressed: selection.suppressed } : {}),
  };
};

/**
 * "Select all": every contact matching the table's filter, less the unticked ones, added to the
 * lists by a background job — the server answers `202` with the job to poll. Explicit ids keep
 * going to `POST /v1/lists/{id}/members` (`addContactsToLists`).
 */
export const startAddAllListMembersJob = async (
  params: StartListMembershipJobParams
): Promise<ListMembershipJobApi> => {
  const listIds = Array.from(new Set(params.listIds.map((id) => id.trim()).filter(Boolean)));
  if (listIds.length === 0) {
    throw new Error('Choose at least one list to add contacts to.');
  }

  const excludeContactIds = Array.from(
    new Set((params.excludeContactIds ?? []).map((id) => id.trim()).filter(Boolean))
  );

  const body: CreateListMembershipJobRequestApi = {
    listIds,
    filter: toContactSelectionFilter(params.selection),
    ...(excludeContactIds.length ? { excludeContactIds } : {}),
  };

  const response = await campaignApiPost<ListMembershipJobEntityResponseApi>(
    '/v1/lists/members/add-all',
    body,
    { 'Idempotency-Key': params.idempotencyKey }
  );

  return assertCampaignApiSuccess(response, 'Failed to start adding contacts to lists');
};

/** `GET /v1/lists/members/jobs/{jobId}` — a select-all job's status and progress. */
export const getListMembershipJob = async (jobId: string): Promise<ListMembershipJobApi> => {
  const id = jobId.trim();
  if (!id) {
    throw new Error('Job id is required');
  }

  const response = await campaignApiGet<ListMembershipJobEntityResponseApi>(
    `/v1/lists/members/jobs/${encodeURIComponent(id)}`
  );

  return assertCampaignApiSuccess(response, 'Failed to read the list job');
};
